import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "./AnimatePresenceWrapper";
import { PinInput } from "@mantine/core";
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Clock,
  CheckCircle,
} from "lucide-react";
import { PerformRequest } from "../lib/usePerformRequest";
import { Endpoints } from "../lib/endpoints";
import { HttpCodes } from "../lib/data";
import { toast } from "sonner";
import { Loader } from "./ui/loader";
import { IOrganizationProps } from "../types/Organization";
import { IUserProps } from "../types/User";
import {
  useUser,
  getOrgTokenKey,
  getOrgRefreshTokenKey,
} from "../context/UserContext";

interface LoginProps {
  onClose: () => void;
  organizationName: string;
  organizationId: number;
  organizationSlug?: string;
}

type AuthStep =
  | "login"
  | "register"
  | "otp-verification"
  | "forgot-password"
  | "forgot-password-otp";

export function Login({
  onClose,
  organizationName,
  organizationId,
  organizationSlug,
}: LoginProps) {
  const { setUserProfile } = useUser();
  const [authStep, setAuthStep] = useState<AuthStep>("login");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Resend OTP cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetRegisterToOrganization().method,
        route: Endpoints.GetRegisterToOrganization().route,
        data: {
          organization_id: organizationId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password,
        },
        doNotUseAuthorization: true,
      });

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        setRegistrationEmail(formData.email);
        setAuthStep("otp-verification");
        setResendCooldown(30);
        toast.success(
          "Registration successful! Please check your email for the OTP."
        );
      } else {
        toast.error(
          response.message || "Registration failed. Please try again."
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const HandleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await PerformRequest<{
        data?: {
          access_token?: string;
          refresh_token?: string;
          organization: IOrganizationProps;
          user: IUserProps;
        };
      }>({
        method: Endpoints.GetLoginToOrganization().method,
        route: Endpoints.GetLoginToOrganization().route,
        data: {
          organization_id: organizationId,
          email: formData.email,
          password: formData.password,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === HttpCodes.Success) {
        // Store tokens with organization-specific key
        const responseData = response.data as any;

        // organizationSlug is required - use it or throw error
        if (!organizationSlug) {
          toast.error("Organization slug is required for login");
          return;
        }

        const tokenKey = getOrgTokenKey(organizationSlug);
        const refreshTokenKey = getOrgRefreshTokenKey(organizationSlug);

        if (responseData?.data?.access_token) {
          localStorage.setItem(tokenKey, responseData.data.access_token);
        }
        if (responseData?.data?.refresh_token) {
          localStorage.setItem(
            refreshTokenKey,
            responseData.data.refresh_token
          );
        }

        // Store user profile in context with organization info
        if (responseData?.data?.user && responseData?.data?.organization) {
          setUserProfile({
            user: responseData.data.user,
            organization: responseData.data.organization,
            organizationId: organizationId,
            organizationSlug: organizationSlug,
          });
        }

        toast.success("Login successful!");
        onClose();
        // Optionally reload the page or redirect
        window.location.reload();
      } else if (response.status === HttpCodes.Forbidden) {
        // If 403 Forbidden, show OTP verification modal
        setRegistrationEmail(formData.email);
        setAuthStep("otp-verification");
        setResendCooldown(30);
        toast.info(
          "Please verify your email with the OTP sent to your email address."
        );
      } else {
        toast.error(
          response.message || "Invalid credentials. Please try again."
        );
      }
    } catch (error: any) {
      // Check if error response status is 403
      if (error?.response?.status === HttpCodes.Forbidden) {
        setRegistrationEmail(formData.email);
        setAuthStep("otp-verification");
        setResendCooldown(30);
        toast.info(
          "Please verify your email with the OTP sent to your email address."
        );
      } else {
        toast.error(error.message || "Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const response = await PerformRequest<{
        data?: {
          access_token?: string;
          refresh_token?: string;
          organization?: IOrganizationProps;
          user?: IUserProps;
        };
      }>({
        method: Endpoints.GetVerifyRegisterOTP().method,
        route: Endpoints.GetVerifyRegisterOTP().route,
        data: {
          email: registrationEmail,
          organization_id: organizationId,
          otp: otp,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === HttpCodes.Success) {
        toast.success("Account verified successfully!");
        // Store tokens with organization-specific key
        const responseData = response.data as any;

        // organizationSlug is required - use it or throw error
        if (!organizationSlug) {
          toast.error("Organization slug is required for registration");
          return;
        }

        const tokenKey = getOrgTokenKey(organizationSlug);
        const refreshTokenKey = getOrgRefreshTokenKey(organizationSlug);

        if (responseData?.data?.access_token) {
          localStorage.setItem(tokenKey, responseData.data.access_token);
        }
        if (responseData?.data?.refresh_token) {
          localStorage.setItem(
            refreshTokenKey,
            responseData.data.refresh_token
          );
        }

        // Store user profile in context with organization info
        if (responseData?.data?.user && responseData?.data?.organization) {
          setUserProfile({
            user: responseData.data.user,
            organization: responseData.data.organization,
            organizationId: organizationId,
            organizationSlug: organizationSlug,
          });
        }

        onClose();
        // Optionally reload the page or redirect
        window.location.reload();
      } else {
        toast.error(response.message || "Invalid OTP. Please try again.");
        setOtp("");
      }
    } catch (error: any) {
      toast.error(
        error.message || "OTP verification failed. Please try again."
      );
      setOtp("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetResendRegisterOTP().method,
        route: Endpoints.GetResendRegisterOTP().route,
        data: {
          email: registrationEmail,
          organization_id: organizationId,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === HttpCodes.Success) {
        setResendCooldown(30);
        toast.success("OTP resent! Please check your email.");
      } else {
        toast.error(
          response.message || "Failed to resend OTP. Please try again."
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetForgotPassword().method,
        route: Endpoints.GetForgotPassword().route,
        data: {
          email: forgotPasswordEmail,
          organization_id: organizationId,
        },
        doNotUseAuthorization: true,
      });

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        setAuthStep("forgot-password-otp");
        setResendCooldown(30);
        toast.success("OTP sent! Please check your email.");
      } else {
        toast.error(
          response.message || "Failed to send OTP. Please try again."
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendForgotPasswordOTP = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetResendForgotPasswordOTP().method,
        route: Endpoints.GetResendForgotPasswordOTP().route,
        data: {
          email: forgotPasswordEmail,
          organization_id: organizationId,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === HttpCodes.Success) {
        setResendCooldown(30);
        toast.success("OTP resent! Please check your email.");
      } else {
        toast.error(
          response.message || "Failed to resend OTP. Please try again."
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmForgotPassword = async () => {
    if (forgotPasswordOtp.length !== 6) {
      toast.error("Please enter a 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetConfirmForgotPassword().method,
        route: Endpoints.GetConfirmForgotPassword().route,
        data: {
          email: forgotPasswordEmail,
          organization_id: organizationId,
          otp: forgotPasswordOtp,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === HttpCodes.Success) {
        toast.success(
          "Password reset successful! You can now login with your new password."
        );
        // Reset states and go back to login
        setForgotPasswordEmail("");
        setForgotPasswordOtp("");
        setAuthStep("login");
      } else {
        toast.error(response.message || "Invalid OTP. Please try again.");
        setForgotPasswordOtp("");
      }
    } catch (error: any) {
      toast.error(
        error.message || "OTP verification failed. Please try again."
      );
      setForgotPasswordOtp("");
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    return `${seconds}s`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-2xl max-w-5xl w-full overflow-hidden grid md:grid-cols-2 max-h-[90vh] overflow-y-auto"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Left Side - Branding & Features */}
        <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-[#17265a] via-[#1f2f6b] to-[#2d3f7a] p-12 text-white relative overflow-hidden">
          {/* Animated Background Orbs */}
          <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-lg mb-6 border border-white/20">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">Welcome to {organizationName}</span>
              </div>
              <h2 className="text-4xl mb-4">
                {authStep === "otp-verification"
                  ? "Verify Your Email"
                  : authStep === "forgot-password" ||
                    authStep === "forgot-password-otp"
                  ? "Reset Password"
                  : authStep === "register"
                  ? "Join Our Community"
                  : "Welcome Back"}
              </h2>
              <p className="text-white/80 leading-relaxed">
                {authStep === "otp-verification"
                  ? "We've sent a 6-digit verification code to your email. Please enter it below to verify your account."
                  : authStep === "forgot-password" ||
                    authStep === "forgot-password-otp"
                  ? "Enter your email and we'll send you a code to reset your password securely."
                  : authStep === "register"
                  ? "Create an account to get access to exclusive events, workshops, and connect with industry leaders."
                  : "Login to access your dashboard, manage tickets, and stay updated with upcoming events."}
              </p>
            </motion.div>

            {authStep !== "otp-verification" &&
              authStep !== "forgot-password" &&
              authStep !== "forgot-password-otp" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-6 mt-12"
                >
                  {[
                    {
                      icon: Shield,
                      title: "Secure & Private",
                      desc: "Your data is encrypted and protected",
                    },
                    {
                      icon: Zap,
                      title: "Instant Access",
                      desc: "Get access to events immediately",
                    },
                    {
                      icon: Sparkles,
                      title: "Premium Features",
                      desc: "Unlock exclusive content and perks",
                    },
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="mb-1">{feature.title}</h4>
                        <p className="text-sm text-white/70">{feature.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}

            {(authStep === "otp-verification" ||
              authStep === "forgot-password-otp") && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6 mt-12"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="mb-1">Check Your Email</h4>
                    <p className="text-sm text-white/70">
                      We sent the code to{" "}
                      {authStep === "otp-verification"
                        ? registrationEmail
                        : forgotPasswordEmail}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="relative z-10 text-sm text-white/60"
          >
            © 2025 {organizationName}. All rights reserved.
          </motion.div>
        </div>

        {/* Right Side - Forms */}
        <div className="p-8 md:p-12 relative">
          {/* Close Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center text-gray-600 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {/* Forgot Password Steps */}
            {authStep === "forgot-password" ||
            authStep === "forgot-password-otp" ? (
              <div className="space-y-6">
                {/* Mobile Branding */}
                <div className="md:hidden mb-8">
                  <h2 className="text-[#17265a] text-3xl mb-2">
                    {authStep === "forgot-password-otp"
                      ? "Verify OTP"
                      : "Reset Password"}
                  </h2>
                  <p className="text-gray-600">
                    {authStep === "forgot-password-otp"
                      ? `Enter the 6-digit code sent to ${forgotPasswordEmail}`
                      : "Enter your email to receive a reset code"}
                  </p>
                </div>

                {/* Desktop Title */}
                <div className="hidden md:block mb-8">
                  <h3 className="text-[#17265a] text-2xl mb-2">
                    {authStep === "forgot-password-otp"
                      ? "Enter Verification Code"
                      : "Reset Your Password"}
                  </h3>
                  <p className="text-gray-600">
                    {authStep === "forgot-password-otp"
                      ? `We sent a code to ${forgotPasswordEmail}`
                      : "We'll send you a code to reset your password"}
                  </p>
                </div>

                {authStep === "forgot-password" ? (
                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    {/* Email Field */}
                    <div>
                      <label className="block text-sm text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <Mail className="w-5 h-5" />
                        </div>
                        <input
                          type="email"
                          value={forgotPasswordEmail}
                          onChange={(e) =>
                            setForgotPasswordEmail(e.target.value)
                          }
                          placeholder="you@example.com"
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-[#17265a] focus:bg-white transition-all duration-200 outline-none text-gray-900"
                          required
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={isLoading || !forgotPasswordEmail}
                      whileHover={
                        !isLoading && forgotPasswordEmail ? { scale: 1.02 } : {}
                      }
                      whileTap={
                        !isLoading && forgotPasswordEmail ? { scale: 0.98 } : {}
                      }
                      className={`w-full py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl group relative overflow-hidden ${
                        isLoading || !forgotPasswordEmail
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-[#17265a] text-white hover:bg-[#0f1a3f]"
                      }`}
                    >
                      {isLoading ? (
                        <Loader size="sm" className="relative z-10" />
                      ) : (
                        <>
                          <span className="relative z-10">Send Reset Code</span>
                          <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </motion.button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    {/* OTP Input */}
                    <div className="flex justify-center">
                      <PinInput
                        length={6}
                        value={forgotPasswordOtp}
                        onChange={setForgotPasswordOtp}
                        type="number"
                        size="lg"
                        style={{
                          gap: "0.75rem",
                        }}
                        styles={{
                          input: {
                            width: "3rem",
                            height: "3.5rem",
                            fontSize: "1.25rem",
                            fontWeight: 600,
                            border: "2px solid #e5e7eb",
                            borderRadius: "0.5rem",
                          },
                        }}
                      />
                    </div>

                    {/* Resend OTP */}
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">
                        Didn't receive the code?
                      </p>
                      <button
                        type="button"
                        onClick={handleResendForgotPasswordOTP}
                        disabled={resendCooldown > 0 || isLoading}
                        className={`text-sm ${
                          resendCooldown > 0 || isLoading
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-[#17265a] hover:underline"
                        } flex items-center justify-center gap-2 mx-auto`}
                      >
                        {resendCooldown > 0 ? (
                          <>
                            <Clock className="w-4 h-4" />
                            Resend in {formatTime(resendCooldown)}
                          </>
                        ) : (
                          "Resend OTP"
                        )}
                      </button>
                    </div>

                    {/* Verify Button */}
                    <motion.button
                      type="button"
                      onClick={handleConfirmForgotPassword}
                      disabled={forgotPasswordOtp.length !== 6 || isLoading}
                      whileHover={
                        forgotPasswordOtp.length === 6 && !isLoading
                          ? { scale: 1.02 }
                          : {}
                      }
                      whileTap={
                        forgotPasswordOtp.length === 6 && !isLoading
                          ? { scale: 0.98 }
                          : {}
                      }
                      className={`w-full py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg ${
                        forgotPasswordOtp.length === 6 && !isLoading
                          ? "bg-[#17265a] text-white hover:bg-[#0f1a3f] hover:shadow-xl"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <span>Verify & Reset Password</span>
                      <CheckCircle className="w-5 h-5" />
                    </motion.button>
                  </div>
                )}

                {/* Back to Login */}
                <div className="text-center text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthStep("login");
                      setForgotPasswordEmail("");
                      setForgotPasswordOtp("");
                    }}
                    className="text-[#17265a] hover:underline"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            ) : authStep === "otp-verification" ? (
              <div className="space-y-6">
                {/* Mobile Branding */}
                <div className="md:hidden mb-8">
                  <h2 className="text-[#17265a] text-3xl mb-2">
                    Verify Your Email
                  </h2>
                  <p className="text-gray-600">
                    Enter the 6-digit code sent to {registrationEmail}
                  </p>
                </div>

                {/* Desktop Title */}
                <div className="hidden md:block mb-8">
                  <h3 className="text-[#17265a] text-2xl mb-2">
                    Enter Verification Code
                  </h3>
                  <p className="text-gray-600">
                    We sent a code to {registrationEmail}
                  </p>
                </div>

                {/* OTP Input */}
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <PinInput
                      length={6}
                      value={otp}
                      onChange={setOtp}
                      type="number"
                      size="lg"
                      style={{
                        gap: "0.75rem",
                      }}
                      styles={{
                        input: {
                          width: "3rem",
                          height: "3.5rem",
                          fontSize: "1.25rem",
                          fontWeight: 600,
                          border: "2px solid #e5e7eb",
                          borderRadius: "0.5rem",
                        },
                      }}
                    />
                  </div>

                  {/* Resend OTP */}
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      Didn't receive the code?
                    </p>
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendCooldown > 0 || isLoading}
                      className={`text-sm ${
                        resendCooldown > 0 || isLoading
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-[#17265a] hover:underline"
                      } flex items-center justify-center gap-2 mx-auto`}
                    >
                      {resendCooldown > 0 ? (
                        <>
                          <Clock className="w-4 h-4" />
                          Resend in {formatTime(resendCooldown)}
                        </>
                      ) : (
                        "Resend OTP"
                      )}
                    </button>
                  </div>

                  {/* Verify Button */}
                  <motion.button
                    type="button"
                    onClick={handleVerifyOTP}
                    disabled={otp.length !== 6 || isLoading}
                    whileHover={
                      otp.length === 6 && !isLoading ? { scale: 1.02 } : {}
                    }
                    whileTap={
                      otp.length === 6 && !isLoading ? { scale: 0.98 } : {}
                    }
                    className={`w-full py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg ${
                      otp.length === 6 && !isLoading
                        ? "bg-[#17265a] text-white hover:bg-[#0f1a3f] hover:shadow-xl"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    <span>Verify Email</span>
                    <CheckCircle className="w-5 h-5" />
                  </motion.button>
                </div>

                {/* Back to Login */}
                <div className="text-center text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthStep("login");
                      setOtp("");
                    }}
                    className="text-[#17265a] hover:underline"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Mobile Branding */}
                <div className="md:hidden mb-8">
                  <h2 className="text-[#17265a] text-3xl mb-2">
                    {authStep === "register"
                      ? "Create Account"
                      : "Welcome Back"}
                  </h2>
                  <p className="text-gray-600">
                    {authStep === "register"
                      ? "Join our community today"
                      : "Login to continue"}
                  </p>
                </div>

                {/* Desktop Title */}
                <div className="hidden md:block mb-8">
                  <h3 className="text-[#17265a] text-2xl mb-2">
                    {authStep === "register"
                      ? "Create Your Account"
                      : "Login to Continue"}
                  </h3>
                  <p className="text-gray-600">
                    {authStep === "register"
                      ? "Fill in your details below"
                      : "Enter your credentials"}
                  </p>
                </div>

                {/* Form */}
                <form
                  onSubmit={
                    authStep === "register" ? handleRegister : HandleLogin
                  }
                  className="space-y-5"
                >
                  {/* Name Fields (Register Only) */}
                  <AnimatePresence mode="wait">
                    {authStep === "register" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="grid grid-cols-2 gap-4"
                      >
                        <div>
                          <label className="block text-sm text-gray-700 mb-2">
                            First Name
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="John"
                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-[#17265a] focus:bg-white transition-all duration-200 outline-none text-gray-900"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-2">
                            Last Name
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Doe"
                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-[#17265a] focus:bg-white transition-all duration-200 outline-none text-gray-900"
                            required
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <Mail className="w-5 h-5" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-[#17265a] focus:bg-white transition-all duration-200 outline-none text-gray-900"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <Lock className="w-5 h-5" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="••••••••"
                        className="w-full pl-12 pr-12 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-[#17265a] focus:bg-white transition-all duration-200 outline-none text-gray-900"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password Field (Register Only) */}
                  <AnimatePresence mode="wait">
                    {authStep === "register" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <label className="block text-sm text-gray-700 mb-2">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                            <Lock className="w-5 h-5" />
                          </div>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            placeholder="••••••••"
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:border-[#17265a] focus:bg-white transition-all duration-200 outline-none text-gray-900"
                            required
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Remember Me & Forgot Password (Login Only) */}
                  {authStep === "login" && (
                    <div className="flex items-center justify-between text-sm">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-2 border-gray-300 text-[#17265a] focus:ring-[#17265a] focus:ring-offset-0 cursor-pointer"
                        />
                        <span className="text-gray-700">Remember me</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setAuthStep("forgot-password");
                          setForgotPasswordEmail(formData.email);
                        }}
                        className="text-[#17265a] hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  {/* Terms & Conditions (Register Only) */}
                  {authStep === "register" && (
                    <div className="text-sm text-gray-600">
                      By signing up, you agree to our{" "}
                      <button
                        type="button"
                        className="text-[#17265a] hover:underline"
                      >
                        Terms of Service
                      </button>{" "}
                      and{" "}
                      <button
                        type="button"
                        className="text-[#17265a] hover:underline"
                      >
                        Privacy Policy
                      </button>
                    </div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    whileHover={!isLoading ? { scale: 1.02 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                    className={`w-full py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl group relative overflow-hidden ${
                      isLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-[#17265a] text-white hover:bg-[#0f1a3f]"
                    }`}
                  >
                    {isLoading ? (
                      <Loader size="sm" className="relative z-10" />
                    ) : (
                      <>
                        <span className="relative z-10">
                          {authStep === "register" ? "Create Account" : "Login"}
                        </span>
                        <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                        <motion.div
                          className="absolute inset-0 bg-white/10"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "100%" }}
                          transition={{ duration: 0.5 }}
                        />
                      </>
                    )}
                  </motion.button>
                </form>

                {/* Toggle Sign Up / Login */}
                <div className="mt-6 text-center text-sm">
                  <span className="text-gray-600">
                    {authStep === "register"
                      ? "Already have an account?"
                      : "Don't have an account?"}{" "}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthStep(
                        authStep === "register" ? "login" : "register"
                      );
                      setFormData({
                        firstName: "",
                        lastName: "",
                        email: "",
                        password: "",
                        confirmPassword: "",
                      });
                    }}
                    className="text-[#17265a] hover:underline"
                  >
                    {authStep === "register" ? "Login" : "Sign up"}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
